import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type ComponentProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import { CompanyAutocompleteSelect } from "@/common/components/Autocomplete/CompanyAutocompleteSelect";
import { UserAutocompleteSelect } from "@/common/components/Autocomplete/UserAutocompleteSelect";
import { EnvironmentPicker } from "@/common/components/EnvironmentPicker";
import { RuleSimpleCompanySelector } from "@/common/components/Rule/RuleSimpleCompanySelector";
import { RuleSimpleUserSelector } from "@/common/components/Rule/RuleSimpleUserSelector";
import { SectionHeading } from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import api from "@/common/utils/api";
import { companyQueryKeys } from "@/company/data/companyQueryKeys";
import { FeatureCompanyUrl, UserUrl } from "@reflag/shared/urls";

import { AttributeDeletionDialog } from "./AttributeDeletionDialog";
import { BackgroundOperationsSection } from "./BackgroundOperations";

type ManageEntity = {
  id: string;
  name: string;
};

type EntityKind = "users" | "companies";
type DeleteOptions = {
  deleteUsersBelongingToCompanies?: boolean;
};

function useEntityMutations(entity: EntityKind) {
  const { appId, envId } = useEnvironment();
  const queryClient = useQueryClient();
  const toast = useToast();
  const errorToast = useErrorToast();

  async function invalidateEntityQueries(targetEnvId: string) {
    if (entity === "users") {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: commonQueryKeys.users(appId, targetEnvId),
        }),
        queryClient.invalidateQueries({
          queryKey: commonQueryKeys.usersCount(appId, targetEnvId),
        }),
        queryClient.invalidateQueries({
          queryKey: commonQueryKeys.userNames(appId, targetEnvId),
        }),
      ]);
      return;
    }

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.list(appId, targetEnvId),
      }),
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.count(appId, targetEnvId),
      }),
    ]);
  }

  async function invalidateMembershipQueries(targetEnvId: string) {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: commonQueryKeys.users(appId, targetEnvId),
      }),
      queryClient.invalidateQueries({
        queryKey: commonQueryKeys.usersCount(appId, targetEnvId),
      }),
      queryClient.invalidateQueries({
        queryKey: commonQueryKeys.userNames(appId, targetEnvId),
      }),
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.list(appId, targetEnvId),
      }),
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.count(appId, targetEnvId),
      }),
    ]);
  }

  async function upsertSingle(item: ManageEntity, targetEnvId: string = envId) {
    if (entity === "users") {
      await api.put<"/apps/:appId/envs/:envId/users/:userId">(
        `/apps/${appId}/envs/${targetEnvId}/users/${encodeURIComponent(item.id)}`,
        { name: item.name },
      );
    } else {
      await api.put<"/apps/:appId/envs/:envId/companies/:companyId">(
        `/apps/${appId}/envs/${targetEnvId}/companies/${encodeURIComponent(item.id)}`,
        { name: item.name },
      );
    }

    await invalidateEntityQueries(targetEnvId);
    toast({
      title: `${entity === "users" ? "User" : "Company"} created or updated`,
      status: "success",
    });
  }

  async function deleteByIds(
    ids: string[],
    targetEnvId: string = envId,
    options?: DeleteOptions,
  ) {
    const response =
      entity === "users"
        ? await api.delete<"/apps/:appId/envs/:envId/users">(
            `/apps/${appId}/envs/${targetEnvId}/users`,
            { data: { ids } },
          )
        : await api.delete<"/apps/:appId/envs/:envId/companies">(
            `/apps/${appId}/envs/${targetEnvId}/companies`,
            {
              data: {
                ids,
                deleteUsers: options?.deleteUsersBelongingToCompanies ?? true,
              },
            },
          );

    await invalidateMembershipQueries(targetEnvId);

    const { deletedIds, notFoundIds } = response.data;
    const singularLabel = entity === "users" ? "user" : "company";
    const pluralLabel = entity === "users" ? "users" : "companies";

    toast({
      title: deletedIds.length
        ? `${deletedIds.length} ${deletedIds.length === 1 ? singularLabel : pluralLabel} deleted`
        : `No ${pluralLabel} deleted`,
      description: notFoundIds.length
        ? `${notFoundIds.length} selected ${notFoundIds.length === 1 ? singularLabel : pluralLabel} could not be found.`
        : undefined,
      status: notFoundIds.length ? "warning" : "success",
    });
  }

  async function removeUserFromCompany(
    companyId: string,
    userId: string,
    targetEnvId: string = envId,
  ) {
    await api.delete<"/apps/:appId/envs/:envId/companies/:companyId/users/:userId">(
      `/apps/${appId}/envs/${targetEnvId}/companies/${encodeURIComponent(companyId)}/users/${encodeURIComponent(userId)}`,
    );

    await invalidateMembershipQueries(targetEnvId);
    toast({
      title: "Member removed",
      status: "success",
    });
  }

  return {
    upsertSingle,
    deleteByIds,
    removeUserFromCompany,
    showError: (title: string, description?: string) =>
      errorToast({ title, description }),
  };
}

function ManageEntitySection({
  entity,
  title,
}: {
  entity: EntityKind;
  title: string;
}) {
  const { app, env } = useEnvironment();
  const navigate = useNavigate();
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [deleteIds, setDeleteIds] = useState<string[]>([]);
  const [memberCompanyId, setMemberCompanyId] = useState("");
  const [memberUserId, setMemberUserId] = useState("");
  const [deleteCompanyUsers, setDeleteCompanyUsers] = useState(true);
  const [activeEnvId, setActiveEnvId] = useState(env.id);
  const [isSubmittingSingle, setIsSubmittingSingle] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const createDisclosure = useDisclosure();
  const attributeDeletionDisclosure = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const deleteConfirmationDisclosure = useDisclosure();
  const removeMemberDisclosure = useDisclosure();
  const removeMemberConfirmationDisclosure = useDisclosure();
  const deleteConfirmationCancelRef = useRef<HTMLButtonElement | null>(null);
  const removeMemberConfirmationCancelRef = useRef<HTMLButtonElement | null>(
    null,
  );
  const removeMemberUserSelectRef = useRef<HTMLButtonElement | null>(null);
  const { upsertSingle, deleteByIds, removeUserFromCompany, showError } =
    useEntityMutations(entity);

  const itemLabel = useMemo(
    () => (entity === "users" ? "user" : "company"),
    [entity],
  );
  const itemsLabel = useMemo(
    () => (entity === "users" ? "users" : "companies"),
    [entity],
  );
  const deleteButtonLabel = deleteIds.length
    ? `Delete ${deleteIds.length} ${deleteIds.length === 1 ? itemLabel : itemsLabel}`
    : `Delete ${itemsLabel}`;
  const activeEnvironmentName =
    app.environments.find((environment) => environment.id === activeEnvId)
      ?.name ?? activeEnvId;
  const reappearNote = useMemo(
    () =>
      entity === "users"
        ? "Users are automatically created as they use your application, so deleted users may reappear."
        : "Companies are automatically created when users from those companies use your application, so deleted companies may reappear.",
    [entity],
  );
  const canSubmitCreate = id.trim().length > 0 && name.trim().length > 0;

  function resetCreateForm() {
    setId("");
    setName("");
  }

  function resetDeleteForm() {
    setDeleteIds([]);
    setDeleteCompanyUsers(true);
  }

  function resetRemoveMemberForm() {
    setMemberCompanyId("");
    setMemberUserId("");
  }

  function resetDialogEnvironment() {
    setActiveEnvId(env.id);
  }

  function handleCreateClose() {
    resetCreateForm();
    resetDialogEnvironment();
    createDisclosure.onClose();
  }

  function handleDeleteClose() {
    resetDeleteForm();
    resetDialogEnvironment();
    deleteDisclosure.onClose();
    deleteConfirmationDisclosure.onClose();
  }

  function handleDeleteConfirmationOpen() {
    if (deleteIds.length === 0) {
      showError(`Please select at least one ${itemLabel} to delete`);
      return;
    }

    deleteDisclosure.onClose();
    deleteConfirmationDisclosure.onOpen();
  }

  function handleDeleteConfirmationBack() {
    deleteConfirmationDisclosure.onClose();
    deleteDisclosure.onOpen();
  }

  function handleRemoveMemberClose() {
    resetRemoveMemberForm();
    resetDialogEnvironment();
    removeMemberDisclosure.onClose();
    removeMemberConfirmationDisclosure.onClose();
  }

  function handleRemoveMemberConfirmationOpen() {
    if (!memberCompanyId.trim()) {
      showError("Please select a company");
      return;
    }

    if (!memberUserId.trim()) {
      showError("Please select a user");
      return;
    }

    removeMemberDisclosure.onClose();
    removeMemberConfirmationDisclosure.onOpen();
  }

  function handleRemoveMemberConfirmationBack() {
    removeMemberConfirmationDisclosure.onClose();
    removeMemberDisclosure.onOpen();
  }

  function handleEnvironmentChange(nextEnvId: string | null) {
    if (!nextEnvId || nextEnvId === activeEnvId) return;

    setActiveEnvId(nextEnvId);
    setDeleteIds([]);
    setMemberCompanyId("");
    setMemberUserId("");
  }

  async function handleSingleSubmit() {
    const payload: ManageEntity = {
      id: id.trim(),
      name: name.trim(),
    };

    if (!payload.id || !payload.name) {
      showError(`Please provide ${itemLabel} id and name`);
      return false;
    }

    setIsSubmittingSingle(true);
    try {
      await upsertSingle(payload, activeEnvId);
      const targetEnvironment = app.environments.find(
        (environment) => environment.id === activeEnvId,
      );
      const targetEnvArgs = {
        id: activeEnvId,
        name: targetEnvironment?.name,
      };

      if (entity === "companies") {
        navigate(FeatureCompanyUrl(targetEnvArgs, payload.id));
      } else {
        navigate(UserUrl(targetEnvArgs, payload.id));
      }
      return true;
    } catch (error: any) {
      showError(
        `Failed to upsert ${itemLabel}`,
        error?.response?.data?.error?.message,
      );
      return false;
    } finally {
      setIsSubmittingSingle(false);
    }
  }

  async function handleDelete() {
    if (deleteIds.length === 0) {
      showError(`Please select at least one ${itemLabel} to delete`);
      return;
    }
    setIsDeleting(true);
    try {
      await deleteByIds(deleteIds, activeEnvId, {
        deleteUsersBelongingToCompanies: deleteCompanyUsers,
      });
      handleDeleteClose();
    } catch (error: any) {
      showError(
        `Failed to delete ${itemsLabel}`,
        error?.response?.data?.error?.message,
      );
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleRemoveMember() {
    if (entity !== "companies") return;

    const normalizedCompanyId = memberCompanyId.trim();
    const normalizedUserId = memberUserId.trim();

    if (!normalizedCompanyId) {
      showError("Please select a company");
      return;
    }

    if (!normalizedUserId) {
      showError("Please select a user");
      return;
    }

    setIsRemovingMember(true);
    try {
      await removeUserFromCompany(
        normalizedCompanyId,
        normalizedUserId,
        activeEnvId,
      );
      handleRemoveMemberClose();
    } catch (error: any) {
      showError(
        "Failed to remove member",
        error?.response?.data?.error?.message,
      );
    } finally {
      setIsRemovingMember(false);
    }
  }

  useEffect(() => {
    if (entity !== "companies") return;
    if (!removeMemberDisclosure.isOpen) return;
    if (!memberCompanyId.trim() || memberUserId.trim()) return;

    const timer = window.setTimeout(() => {
      const userSelectorButton = removeMemberUserSelectRef.current;
      if (!userSelectorButton || userSelectorButton.disabled) return;

      userSelectorButton.focus();
      userSelectorButton.click();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [entity, memberCompanyId, memberUserId, removeMemberDisclosure.isOpen]);

  return (
    <>
      <Card p={5} variant="filled">
        <Flex direction="column" gap={4}>
          <Heading as="h4" size="sm">
            {title}
          </Heading>
          <Text color="dimmed" fontSize="sm">
            Create or update a single {itemLabel}, or delete one or more.
          </Text>

          <ButtonGroup size="sm">
            <Button variant="outline" onClick={createDisclosure.onOpen}>
              Create or update {itemLabel}
            </Button>
            {entity === "companies" && (
              <Button variant="outline" onClick={removeMemberDisclosure.onOpen}>
                Remove member
              </Button>
            )}
            <Button variant="outline" onClick={deleteDisclosure.onOpen}>
              Delete {itemsLabel}
            </Button>
            <Button
              variant="outline"
              onClick={attributeDeletionDisclosure.onOpen}
            >
              Delete attribute
            </Button>
          </ButtonGroup>
        </Flex>
      </Card>

      {attributeDeletionDisclosure.isOpen && (
        <AttributeDeletionDialog
          entityType={itemLabel}
          onClose={attributeDeletionDisclosure.onClose}
        />
      )}

      <Modal isOpen={createDisclosure.isOpen} onClose={handleCreateClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create or update {itemLabel}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form
              id={`manage-${entity}-create-form`}
              onSubmit={(e) => {
                e.preventDefault();
                void handleSingleSubmit().then((didSave) => {
                  if (didSave) {
                    handleCreateClose();
                  }
                });
              }}
            >
              <VStack align="stretch" spacing={3}>
                <FormControl>
                  <FormLabel>Environment</FormLabel>
                  <EnvironmentPicker
                    size="sm"
                    value={activeEnvId}
                    onChange={handleEnvironmentChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>ID</FormLabel>
                  <Input
                    autoFocus
                    placeholder={`${itemLabel}-id`}
                    value={id}
                    onChange={(e) => setId(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input
                    placeholder={`${itemLabel} name`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </FormControl>
              </VStack>
            </form>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghostDimmed" onClick={handleCreateClose}>
                Cancel
              </Button>
              <Button
                form={`manage-${entity}-create-form`}
                isDisabled={!canSubmitCreate || isSubmittingSingle}
                isLoading={isSubmittingSingle}
                type="submit"
              >
                Save {itemLabel}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {entity === "companies" && (
        <>
          <Modal
            isOpen={removeMemberDisclosure.isOpen}
            onClose={handleRemoveMemberClose}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Remove member</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <form
                  id="manage-companies-remove-member-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleRemoveMemberConfirmationOpen();
                  }}
                >
                  <VStack align="stretch" spacing={3}>
                    <FormControl>
                      <FormLabel>Environment</FormLabel>
                      <EnvironmentPicker
                        size="sm"
                        value={activeEnvId}
                        onChange={handleEnvironmentChange}
                      />
                    </FormControl>
                    <Text color="dimmed" fontSize="sm">
                      Remove a user from a company. The user and company records
                      will be kept.
                    </Text>
                    <FormControl>
                      <FormLabel>Select company</FormLabel>
                      <CompanyAutocompleteSelect
                        canClear
                        envId={activeEnvId}
                        placeholder=""
                        value={memberCompanyId || null}
                        valueAsId
                        onChange={(value) => {
                          setMemberCompanyId(value ?? "");
                          setMemberUserId("");
                        }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Select user</FormLabel>
                      <UserAutocompleteSelect
                        ref={removeMemberUserSelectRef}
                        canClear
                        envId={activeEnvId}
                        isDisabled={!memberCompanyId.trim()}
                        placeholder=""
                        query={
                          memberCompanyId.trim()
                            ? { companyId: memberCompanyId.trim() }
                            : undefined
                        }
                        value={memberUserId || null}
                        valueAsId
                        onChange={(value) => setMemberUserId(value ?? "")}
                      />
                    </FormControl>
                  </VStack>
                </form>
              </ModalBody>
              <ModalFooter>
                <HStack>
                  <Button
                    variant="ghostDimmed"
                    onClick={handleRemoveMemberClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    form="manage-companies-remove-member-form"
                    isDisabled={!memberCompanyId.trim() || !memberUserId.trim()}
                    type="submit"
                  >
                    Continue
                  </Button>
                </HStack>
              </ModalFooter>
            </ModalContent>
          </Modal>

          <AlertDialog
            closeOnEsc={!isRemovingMember}
            closeOnOverlayClick={!isRemovingMember}
            isOpen={removeMemberConfirmationDisclosure.isOpen}
            leastDestructiveRef={removeMemberConfirmationCancelRef}
            onClose={handleRemoveMemberConfirmationBack}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader>Confirm member removal</AlertDialogHeader>
                <AlertDialogBody>
                  <VStack align="stretch" spacing={3}>
                    <Text>
                      You are about to remove user{" "}
                      <Text as="span" fontWeight="semibold">
                        {memberUserId}
                      </Text>{" "}
                      from company{" "}
                      <Text as="span" fontWeight="semibold">
                        {memberCompanyId}
                      </Text>{" "}
                      in the{" "}
                      <Text as="span" fontWeight="semibold">
                        {activeEnvironmentName}
                      </Text>{" "}
                      environment.
                    </Text>
                    <Text>
                      The membership will be removed, but the user and company
                      records will be kept.
                    </Text>
                  </VStack>
                </AlertDialogBody>
                <AlertDialogFooter>
                  <HStack>
                    <Button
                      ref={removeMemberConfirmationCancelRef}
                      isDisabled={isRemovingMember}
                      variant="ghostDimmed"
                      onClick={handleRemoveMemberConfirmationBack}
                    >
                      Back
                    </Button>
                    <Button
                      colorScheme="red"
                      isLoading={isRemovingMember}
                      onClick={() => void handleRemoveMember()}
                    >
                      Remove member
                    </Button>
                  </HStack>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </>
      )}

      <Modal
        closeOnEsc={!isDeleting}
        closeOnOverlayClick={!isDeleting}
        isOpen={deleteDisclosure.isOpen}
        size="lg"
        onClose={handleDeleteClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete {itemsLabel}</ModalHeader>
          <ModalCloseButton isDisabled={isDeleting} />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Environment</FormLabel>
                <EnvironmentPicker
                  size="sm"
                  value={activeEnvId}
                  onChange={handleEnvironmentChange}
                />
              </FormControl>
              <Text color="dimmed" fontSize="sm">
                This removes all stored attributes used in flag evaluation and
                all related flag exposure, tracking, and feedback data. This
                action cannot be undone.
              </Text>
              <Text color="dimmed" fontSize="sm">
                {reappearNote}
              </Text>
              <FormControl>
                <FormLabel>Select {itemsLabel}</FormLabel>
                {entity === "users" ? (
                  <RuleSimpleUserSelector
                    key={activeEnvId}
                    envId={activeEnvId}
                    summaryCount={2}
                    value={deleteIds}
                    onChange={setDeleteIds}
                  />
                ) : (
                  <RuleSimpleCompanySelector
                    key={activeEnvId}
                    envId={activeEnvId}
                    summaryCount={2}
                    value={deleteIds}
                    onChange={setDeleteIds}
                  />
                )}
              </FormControl>
              {entity === "companies" && (
                <FormControl>
                  <Checkbox
                    isChecked={deleteCompanyUsers}
                    onChange={(e) => setDeleteCompanyUsers(e.target.checked)}
                  >
                    Delete users belonging to the selected companies.
                  </Checkbox>
                  <Text color="dimmed" fontSize="sm" mt={1}>
                    Users belonging to any unselected company are preserved.
                  </Text>
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button
                isDisabled={isDeleting}
                variant="ghostDimmed"
                onClick={handleDeleteClose}
              >
                Cancel
              </Button>
              <Button
                isDisabled={deleteIds.length === 0}
                onClick={handleDeleteConfirmationOpen}
              >
                Continue
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        closeOnEsc={!isDeleting}
        closeOnOverlayClick={!isDeleting}
        isOpen={deleteConfirmationDisclosure.isOpen}
        leastDestructiveRef={deleteConfirmationCancelRef}
        onClose={handleDeleteConfirmationBack}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Confirm deletion</AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={3}>
                <Text>
                  You are about to permanently delete {deleteIds.length}{" "}
                  {deleteIds.length === 1 ? itemLabel : itemsLabel} from the{" "}
                  <Text as="span" fontWeight="semibold">
                    {activeEnvironmentName}
                  </Text>{" "}
                  environment.
                </Text>
                {entity === "companies" && deleteCompanyUsers && (
                  <Text>
                    Users belonging only to the selected companies will also be
                    permanently deleted.
                  </Text>
                )}
                <Text fontWeight="semibold">This action cannot be undone.</Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <HStack>
                <Button
                  ref={deleteConfirmationCancelRef}
                  isDisabled={isDeleting}
                  variant="ghostDimmed"
                  onClick={handleDeleteConfirmationBack}
                >
                  Back
                </Button>
                <Button
                  colorScheme="red"
                  isLoading={isDeleting}
                  onClick={() => void handleDelete()}
                >
                  {deleteButtonLabel}
                </Button>
              </HStack>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default function ManageData() {
  const { appId, envId } = useEnvironment();
  const manageDataRestrictedFlagKey = "manage-data" as ComponentProps<
    typeof Restricted
  >["flagKey"];

  return (
    <Flex direction="column" gap={8} maxW="wideForm">
      <Restricted
        alertStyle="callout"
        flagKey={manageDataRestrictedFlagKey}
        role="admin"
      >
        <Flex direction="column" gap={4}>
          <SectionHeading>Manage data</SectionHeading>
          <Text color="dimmed" fontSize="sm" maxW="2xl">
            Create, update and delete users and companies manually.
          </Text>
        </Flex>
        <Flex direction="column" gap={4}>
          <ManageEntitySection entity="companies" title="Companies" />
          <ManageEntitySection entity="users" title="Users" />
          <BackgroundOperationsSection appId={appId} envId={envId} />
        </Flex>
      </Restricted>
    </Flex>
  );
}
